import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { format } from 'date-fns';

import EventActionButtons from '@/components/eventActionButtons/eventActionButtons';
import Layout from '@/components/layout';
import SiteContextProvider from '@/contexts/siteContext';
import { getSingleEventData } from '@/lib/fetchers/events';
import { getSingleSiteData } from '@/lib/fetchers/sites';
import { DEFAULT_REVALIDATE_TIME } from '@/constants';
import type { Event, SitePageProps, SiteParams } from '@/types';
import TechnologyLinks from '@/components/technologyLinks/technologyLinks';

interface EventPageParams extends SiteParams {
  eventId: string;
}

interface EventPageProps extends SitePageProps {
  event: Event;
}

export const getStaticPaths: GetStaticPaths<EventPageParams> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<
  EventPageProps,
  EventPageParams
> = async ({ params }) => {
  if (!params?.eventId) {
    return {
      notFound: true,
    };
  }

  const [event, site] = await Promise.all([
    getSingleEventData(params.eventId),
    getSingleSiteData(params?.site),
  ]);

  return {
    props: { event, site },
    revalidate: Number.parseInt(
      process.env.ISR_REVALIDATE_TIME || DEFAULT_REVALIDATE_TIME,
    ),
  };
};

const Event = ({ event, site }: EventPageProps) => {
  const eventStartDate = new Date(event.start);
  const formattedDate = format(eventStartDate, 'EEEE, LLLL d, y');
  const formattedStartTime = format(eventStartDate, 'K:mma');
  const titleContents = `${event.name} on ${formattedDate}`;

  return (
    <SiteContextProvider site={site}>
      <Head>
        <title key="title">{titleContents}</title>
        <meta
          name="description"
          content={`View details including start time, end time, and options to purchase tickets for ${event.name}`}
          key="meta:description"
        />
      </Head>
      <Layout>
        <div className="row">
          <div className="col-sm-12 col-lg-5">
            {event.url_image && (
              <a
                href={event.url_image}
                target="_blank"
                className="d-block"
                rel="noopener"
              >
                <img
                  src={event.url_image}
                  className="img-fluid p-3"
                  alt={`Event image for ${event.name}`}
                />
              </a>
            )}
          </div>
          <div className="col-sm-12 col-lg-5">
            <h1>{event.name}</h1>
            <time dateTime={event.start} className="fw-bold fs-5 d-block mb-3">
              {formattedDate}
            </time>
            {event.html_artists && (
              <div
                className="server-content single-event-artists mb-4"
                dangerouslySetInnerHTML={{ __html: event.html_artists }}
              />
            )}
            <p>
              Doors {formattedStartTime}
              <br />
              {event.venue_name}
            </p>
            <EventActionButtons className="d-grid mt-5" event={event} />
            <TechnologyLinks
              urlData={event.urls.data}
              className="d-flex justify-content-center mt-4 gap-3"
            />
          </div>
        </div>
        {event.html_description && (
          <div
            className="single-event-desc server-content my-4 pt-4"
            dangerouslySetInnerHTML={{ __html: event.html_description }}
          />
        )}
      </Layout>
    </SiteContextProvider>
  );
};

export default Event;
